import React from 'react';
import PropTypes from 'prop-types';
import Switch from '@mui/material/Switch';

import { UpsellTouchpointKey } from '../../../../Model/UpsellConfig';
import texts from './texts.json';
import {
  ExperimentContainer,
  StyledFormControlLabel,
  SectionDescription,
} from './styles';
import TouchpointEditor from './TouchpointEditor';
import CheckoutPreEnrollEditor from './CheckoutPreEnrollEditor';

const touchpointTitles = {
  [UpsellTouchpointKey.SEVEN_DAYS_BEFORE]: texts.touchpointSevenDays,
  [UpsellTouchpointKey.THREE_DAYS_BEFORE]: texts.touchpointThreeDays,
  [UpsellTouchpointKey.LAST_DAY]: texts.touchpointLastDay,
};

/**
 * Renders the three touchpoint editors of one upsell strategy (the default
 * strategy or a challenge's custom one), its checkout pre-enroll when that is rolled out to the coach, and the pop-up
 * A/B test box when that is. The box edits group B's touchpoints with this same editor, their messages optional.
 */
const StrategyEditor = ({
  strategy,
  onTouchpointChange,
  onCheckoutPreEnrollChange,
  showCheckoutPreEnroll,
  onExperimentChange,
  onExperimentTouchpointChange,
  showExperiment,
  isMessageOptional,
  products,
  currency,
  programs,
  mealPlans,
}) => (
  <>
    {Object.values(UpsellTouchpointKey).map((touchpointKey) => (
      <TouchpointEditor
        key={touchpointKey}
        title={touchpointTitles[touchpointKey]}
        touchpoint={strategy.touchpoints[touchpointKey]}
        onChange={(patch) => onTouchpointChange(touchpointKey, patch)}
        products={products}
        currency={currency}
        programs={programs}
        mealPlans={mealPlans}
        isMessageOptional={isMessageOptional}
      />
    ))}
    {showExperiment && (
      <ExperimentContainer>
        <StyledFormControlLabel
          control={(
            <Switch
              checked={!!strategy.experiment?.enabled}
              onChange={(event) => onExperimentChange({ enabled: event.target.checked })}
              color="primary"
            />
          )}
          label={texts.experimentTitle}
        />
        <SectionDescription>
          {strategy.experiment?.enabled ? texts.experimentDescription : texts.experimentDisabled}
        </SectionDescription>
        {!!strategy.experiment?.enabled && (
          <StrategyEditor
            strategy={strategy.experiment}
            onTouchpointChange={onExperimentTouchpointChange}
            isMessageOptional
            products={products}
            currency={currency}
            programs={programs}
            mealPlans={mealPlans}
          />
        )}
      </ExperimentContainer>
    )}
    {showCheckoutPreEnroll && (
      <CheckoutPreEnrollEditor
        checkoutPreEnroll={strategy.checkoutPreEnroll}
        onChange={onCheckoutPreEnrollChange}
        products={products}
        currency={currency}
      />
    )}
  </>
);

StrategyEditor.propTypes = {
  strategy: PropTypes.object.isRequired,
  onTouchpointChange: PropTypes.func.isRequired,
  onCheckoutPreEnrollChange: PropTypes.func,
  showCheckoutPreEnroll: PropTypes.bool,
  onExperimentChange: PropTypes.func,
  onExperimentTouchpointChange: PropTypes.func,
  showExperiment: PropTypes.bool,
  isMessageOptional: PropTypes.bool,
  products: PropTypes.array.isRequired,
  currency: PropTypes.string.isRequired,
  programs: PropTypes.array.isRequired,
  mealPlans: PropTypes.array.isRequired,
};

StrategyEditor.defaultProps = {
  onCheckoutPreEnrollChange: () => {},
  showCheckoutPreEnroll: false,
  onExperimentChange: () => {},
  onExperimentTouchpointChange: () => {},
  showExperiment: false,
  isMessageOptional: false,
};

export default StrategyEditor;
